import { httpClient } from '@/shared/api/httpClient';

export type ItemUtilizado = {
  referenciaId: string;
  descricao: string;
  tipo: 'Produto' | 'Servico';
  quantidadeOrcamentos: number;
  quantidadeTotal: number;
  valorTotal: number;
};

export type FiltrosItensUtilizados = {
  inicio?: string;
  fim?: string;
  clienteId?: string;
  somenteFechados?: boolean;
  /**
   * Data que recorta o período nas rotas de itens: `abertura` (padrão) ou
   * `fechamento` (data de finalização — só O.S. finalizadas entram).
   */
  baseData?: 'abertura' | 'fechamento';
};

function querystring(params: Record<string, string | undefined>): string {
  const usp = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v !== undefined && v !== '') usp.set(k, v);
  }
  const s = usp.toString();
  return s === '' ? '' : `?${s}`;
}

export type UsoDetalhado = {
  orcamentoId: string;
  orcamentoNumero: string;
  orcamentoData: string;
  orcamentoStatus: string;
  clienteId: string;
  clienteNome: string;
  referenciaId: string;
  descricao: string;
  tipo: 'Produto' | 'Servico';
  colaboradorId: string | null;
  colaboradorNome: string | null;
  quantidade: number;
  precoUnitario: number;
  valorDesconto: number;
  subtotal: number;
};

export type OrigemKm = 'Cadastro' | 'Orcamento' | 'Manual';

export type ItemHistoricoVeiculo = {
  tipo: 'Produto' | 'Servico';
  descricao: string;
  colaboradorNome: string | null;
  quantidade: number;
  precoUnitario: number;
  valorDesconto: number;
  subtotal: number;
};

export type OrcamentoHistoricoVeiculo = {
  id: string;
  numero: string;
  data: string;
  status: string;
  clienteId: string;
  clienteNome: string;
  totalPecas: number;
  totalServicos: number;
  descontoGeral: number;
  total: number;
  itens: ItemHistoricoVeiculo[];
};

export type KmHistoricoVeiculo = {
  id: string;
  data: string;
  km: number;
  origem: OrigemKm;
  observacao: string | null;
  orcamentoId: string | null;
};

export type HistoricoVeiculo = {
  veiculo: {
    id: string;
    /** null quando cadastrado sem placa — use `descricao` para exibir. */
    placa: string | null;
    /** Placa quando existe; senão "Marca Modelo Ano". */
    descricao: string;
    marca: string;
    modelo: string;
    ano: number | null;
    motor: string | null;
    kmAtual: number | null;
    clienteAtualNome: string | null;
  };
  kmHistorico: KmHistoricoVeiculo[];
  orcamentos: OrcamentoHistoricoVeiculo[];
  resumo: {
    totalOrcamentos: number;
    totalFechado: number;
    primeiraVisita: string | null;
    ultimaVisita: string | null;
  };
};

export type VeiculoRelatorioClientes = {
  /** null = O.S. sem veículo vinculado ("Sem veículo"). */
  veiculoId: string | null;
  descricao: string;
  placa: string | null;
  kmAtual: number | null;
  quantidadeOrdens: number;
  ultimaVisita: string | null;
  /** Líquido: descontos de item e rateio do desconto geral já abatidos. */
  totalServicos: number;
  /** Líquido: descontos de item e rateio do desconto geral já abatidos. */
  totalPecas: number;
  /** Informativo (já abatido de peças/serviços). */
  descontos: number;
  total: number;
};

export type ClienteRelatorioClientes = {
  clienteId: string;
  nome: string;
  telefone: string | null;
  documento: string | null;
  quantidadeVeiculos: number;
  quantidadeOrdens: number;
  ultimaVisita: string | null;
  totalServicos: number;
  totalPecas: number;
  descontos: number;
  total: number;
  ticketMedio: number;
  veiculos: VeiculoRelatorioClientes[];
};

export type RelatorioClientes = {
  resumo: {
    clientes: number;
    clientesComCompra: number;
    veiculos: number;
    ordens: number;
    totalServicos: number;
    totalPecas: number;
    descontos: number;
    total: number;
    ticketMedio: number;
  };
  clientes: ClienteRelatorioClientes[];
};

export type FiltrosRelatorioClientes = FiltrosItensUtilizados & {
  incluirSemCompra?: boolean;
  busca?: string;
};

export const relatoriosApi = {
  clientes: (f: FiltrosRelatorioClientes) =>
    httpClient.get<RelatorioClientes>(
      `/api/relatorios/clientes${querystring({
        inicio: f.inicio,
        fim: f.fim,
        clienteId: f.clienteId,
        somenteFechados: f.somenteFechados ? 'true' : undefined,
        incluirSemCompra: f.incluirSemCompra ? 'true' : undefined,
        busca: f.busca?.trim() || undefined,
      })}`,
    ),
  historicoVeiculo: (veiculoId: string) =>
    httpClient.get<HistoricoVeiculo>(`/api/relatorios/veiculo/${veiculoId}`),
  itensUtilizados: (f: FiltrosItensUtilizados) =>
    httpClient.get<ItemUtilizado[]>(
      `/api/relatorios/itens-utilizados${querystring({
        inicio: f.inicio,
        fim: f.fim,
        clienteId: f.clienteId,
        somenteFechados: f.somenteFechados ? 'true' : undefined,
        baseData: f.baseData === 'fechamento' ? 'fechamento' : undefined,
      })}`,
    ),
  itensDetalhados: (f: FiltrosItensUtilizados & { tipo?: 'Produto' | 'Servico' }) =>
    httpClient.get<UsoDetalhado[]>(
      `/api/relatorios/itens-detalhe${querystring({
        inicio: f.inicio,
        fim: f.fim,
        clienteId: f.clienteId,
        somenteFechados: f.somenteFechados ? 'true' : undefined,
        tipo: f.tipo,
        baseData: f.baseData === 'fechamento' ? 'fechamento' : undefined,
      })}`,
    ),
};
